import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Unified Button Component - Behtari Design System
 * 
 * Features:
 * - WCAG 2.1 AAA compliant touch targets (44px minimum)
 * - Loading states with spinner
 * - Icon support (left/right)
 * - Persona-specific variants
 * - Proper focus management
 * - Reduced motion support
 */

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 active:scale-[0.97] motion-reduce:active:scale-100 motion-reduce:transition-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-md hover:bg-primary/90 hover:shadow-lg",
        destructive: "bg-destructive text-destructive-foreground shadow-md hover:bg-destructive/90 hover:shadow-lg",
        outline: "border-2 border-input bg-background/50 hover:bg-accent hover:text-accent-foreground shadow-sm hover:shadow-md hover:border-primary/50",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md",
        ghost: "hover:bg-accent/50 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Persona-specific gradients
        "job-seeker": "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-lg hover:shadow-xl",
        "individual-employer": "bg-gradient-to-r from-fuchsia-500 to-fuchsia-600 text-white hover:from-fuchsia-600 hover:to-fuchsia-700 shadow-lg hover:shadow-xl",
        "business-employer": "bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-xl",
        "agency": "bg-gradient-to-r from-sky-500 to-sky-600 text-white hover:from-sky-600 hover:to-sky-700 shadow-lg hover:shadow-xl",
        "provider": "bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl",
        // Status variants
        success: "bg-emerald-500 text-white hover:bg-emerald-600 shadow-md hover:shadow-lg",
        warning: "bg-amber-500 text-white hover:bg-amber-600 shadow-md hover:shadow-lg",
        // Special variants
        glass: "bg-white/10 backdrop-blur-lg border border-white/20 text-white hover:bg-white/20 shadow-lg hover:shadow-xl",
        premium: "bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:from-amber-500 hover:to-orange-600 shadow-xl hover:shadow-2xl border border-amber-300/20",
      },
      size: {
        // All sizes meet 44px minimum touch target (WCAG 2.1 AAA)
        default: "min-h-[44px] px-6 py-2 text-sm",
        sm: "min-h-[44px] rounded-lg px-4 text-xs",
        lg: "min-h-[48px] rounded-xl px-8 text-base",
        xl: "min-h-[52px] rounded-xl px-10 text-lg",
        icon: "min-h-[44px] min-w-[44px] p-0",
        "icon-sm": "min-h-[44px] min-w-[44px] p-0",
        "icon-lg": "min-h-[48px] min-w-[48px] p-0 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    asChild = false, 
    loading = false,
    loadingText,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : "button"
    const isDisabled = disabled || loading
    
    // Determine icon size based on button size
    const iconSizeClass = size === 'sm' || size === 'icon-sm' ? 'h-4 w-4' : 
                          size === 'lg' || size === 'xl' || size === 'icon-lg' ? 'h-5 w-5' : 
                          'h-4 w-4'
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className={cn(iconSizeClass, "animate-spin")} aria-hidden="true" />
            {loadingText && <span>{loadingText}</span>}
            {!loadingText && children && <span className="sr-only">{children}</span>}
          </>
        ) : (
          <>
            {leftIcon && <span className={iconSizeClass} aria-hidden="true">{leftIcon}</span>}
            {children}
            {rightIcon && <span className={iconSizeClass} aria-hidden="true">{rightIcon}</span>}
          </>
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
